import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useNavigate } from "react-router-dom"
import { useTournamentList } from "@/graphql/hooks/useTournamentList"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import {
  TournamentStatusBadge,
  TournamentPresentialBadge,
} from "@/components/ui/TournamentStatusBadge"
import { Trophy, Clock, MapPin, Users, Calendar, Eye } from "lucide-react"
import RecentTournaments from "./RecentTournaments"
interface UserTournamentsProps {
  personId: number
}

export const UserTournaments = ({ personId }: UserTournamentsProps) => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>("all")

  // Get user's tournaments by using their person ID as player filter
  const { tournaments, loading, error, refetch } = useTournamentList({
    input: {
      gameId: 1, // Default game ID
      players: [personId],
      page: 1,
      limit: 10,
      date: "DESC",
    },
  })

  if (loading) {
    return <LoadingScreen message="Cargando tus torneos..." />
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Error al cargar los torneos. Intenta de nuevo.</p>
          <Button onClick={() => refetch()} className="mt-4">
            Reintentar
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Filter tournaments by status
  const filteredTournaments = tournaments.filter((tournament) => {
    // 1: Programado
    // 2: Inscripciones
    // 3: Iniciado
    // 4: Proceso
    // 5: Terminado
    // 6: Reportado

    if (statusFilter === "all") return true
    if (statusFilter === "ongoing") return [3, 4].includes(tournament.statusId) // En curso
    if (statusFilter === "finished") return [5, 6].includes(tournament.statusId) // Finalizado
    if (statusFilter === "upcoming") return [1, 2].includes(tournament.statusId) // Próximo
    return true
  })

  // Separate ongoing and recent tournaments
  const ongoingTournaments = filteredTournaments.filter(
    (t) => [1, 2, 3, 4].includes(t.statusId) && t.role === "JUGADOR",
  )

  // Separate ongoing and recent tournaments
  const organizeTournaments = filteredTournaments.filter((t) => t.role === "JUEZ")
  const recentTournaments = filteredTournaments
    .filter((t) => [5, 6].includes(t.statusId))
    .slice(0, 10)

  return (
    <div className="space-y-6">
      {/* Torneos organizados */}
      {organizeTournaments.length > 0 && (
        <Card className="bg-card dark:bg-gray-800 border border-border dark:border-gray-700 transition-colors duration-300">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary dark:text-primary/80" />
              <CardTitle className="text-gray-900 dark:text-gray-100">Torneos en gestión</CardTitle>
              <Badge
                variant="secondary"
                className="bg-secondary/20 text-secondary dark:bg-secondary/30 dark:text-secondary-foreground"
              >
                {organizeTournaments.length}
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            {organizeTournaments.length > 0 ? (
              <div className="space-y-4">
                {organizeTournaments.map((tournament) => (
                  <Card
                    key={tournament.id}
                    className="border-l-4 border-l-primary bg-white dark:bg-gray-800"
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-primary dark:text-primary/80" />
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              {tournament.name}
                            </h3>
                            <TournamentStatusBadge statusId={tournament.statusId} />
                            <TournamentPresentialBadge state={tournament.isPresential} />
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground dark:text-gray-300">
                            <div className="flex items-center gap-1">
                              <Trophy className="h-3 w-3" />
                              {tournament.Game?.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {tournament.Format?.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {tournament.Store?.name}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(tournament.startDate).toLocaleDateString("es-ES", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(tournament.urlView)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Administrar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-muted-foreground dark:text-gray-400 mx-auto mb-4" />
                <p className="text-muted-foreground dark:text-gray-300">
                  No tienes torneos en curso actualmente
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ongoing Tournaments */}
      <Card className="bg-card dark:bg-gray-800 border border-border dark:border-gray-700 transition-colors duration-300">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle>Torneos en Curso</CardTitle>
            <Badge variant="secondary">{ongoingTournaments.length}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {ongoingTournaments.length > 0 ? (
            <div className="space-y-4">
              {ongoingTournaments.map((tournament) => (
                <Card
                  key={tournament.id}
                  className="border-l-4 border-l-primary bg-card dark:bg-gray-800 border-border dark:border-gray-700 transition-colors duration-300"
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-primary dark:text-primary/80" />
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {tournament.name}
                          </h3>
                          <TournamentStatusBadge statusId={tournament.statusId} />
                          <TournamentPresentialBadge state={tournament.isPresential} />
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground dark:text-gray-300">
                          <div className="flex items-center gap-1">
                            <Trophy className="h-3 w-3" />
                            {tournament.Game?.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {tournament.Format?.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {tournament.Store?.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(tournament.startDate).toLocaleDateString("es-ES", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-gray-900 dark:text-gray-100 border-border dark:border-gray-600"
                          onClick={() => navigate(tournament.urlView)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Torneo
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tienes torneos en curso actualmente</p>
            </div>
          )}
        </CardContent>
      </Card>

      <RecentTournaments
        recentTournaments={recentTournaments}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />
    </div>
  )
}
